import { fmtDate } from '@dcc-erp/shared';

import type { ReactNode } from 'react';

// ============================================================
// Shared, data-shape-agnostic approval timeline.
//
// Renders the maker → reviewer(s) → approver chain:
//   • lead row    = ผู้จัดทำ (the submitter)         — supplied via `leadLine`
//   • each step   = ผู้ตรวจสอบ, except the final step = ผู้อนุมัติ
//                   (derived: stepNo === approvals.length)
//   • trailing    = posted-by/at (JV) or paid-by/at (payreq) — `trailLine`
//
// `isActive` + `currentStep` highlight the awaiting step (only meaningful while
// the document is PENDING). JV and payreq both feed it their persisted approval
// rows mapped into the `approvals` shape.
// ============================================================

export interface ApprovalTimelineRow {
  id: string | number;
  stepNo: number;
  roleLabel: string;
  approvedBy?: string | null | undefined;
  comment?: string | null | undefined;
  approvedAt?: string | Date | null | undefined;
}

export interface ApprovalTimelineProps {
  approvals: ApprovalTimelineRow[];
  /** True while the document is awaiting action (PENDING) — drives step highlight. */
  isActive: boolean;
  /** The step currently awaiting action (1-based). */
  currentStep: number;
  /** Lead slot — the submitter line (ผู้จัดทำ). */
  leadLine?: ReactNode;
  /** Trail slot — posted-by/at (JV) or paid-by/at (payreq). */
  trailLine?: ReactNode;
  /** Shown when there are no approval rows and no lead line. */
  emptyText?: string;
}

export function ApprovalTimeline({
  approvals,
  isActive,
  currentStep,
  leadLine,
  trailLine,
  emptyText = 'ยังไม่ส่งอนุมัติ',
}: ApprovalTimelineProps) {
  if (approvals.length === 0 && !leadLine) {
    return <div className="meta">{emptyText}</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {leadLine}
      {approvals.map((a) => {
        const done = !!a.approvedAt;
        const current = isActive && !done && a.stepNo === currentStep;
        const bg = done ? '#e7f4ec' : current ? '#fef3e8' : '#fafafa';
        const bd = done ? '#bbdebf' : current ? '#f0dab0' : 'var(--border)';
        return (
          <div
            key={a.id}
            style={{
              display: 'flex',
              gap: 12,
              padding: '10px 12px',
              background: bg,
              borderRadius: 6,
              border: `1px solid ${bd}`,
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'grid',
                placeItems: 'center',
                fontSize: 13,
                fontWeight: 600,
                background: done ? 'var(--positive)' : current ? 'var(--warning)' : '#e5e5e9',
                color: done || current ? 'white' : 'var(--text-muted)',
              }}
            >
              {done ? '✓' : a.stepNo}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{a.roleLabel}</div>
              <div className="meta">{a.approvedBy ?? '—'}</div>
              {a.comment && (
                <div className="meta" style={{ fontStyle: 'italic' }}>
                  &ldquo;{a.comment}&rdquo;
                </div>
              )}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', alignSelf: 'center' }}>
              {done && a.approvedAt
                ? `อนุมัติ ${fmtDate(a.approvedAt)}`
                : current
                  ? 'รออนุมัติ'
                  : '—'}
            </div>
          </div>
        );
      })}
      {trailLine}
    </div>
  );
}
